import React, { useState, useEffect } from 'react';
import { useSelector } from "react-redux";
import { useParams } from 'react-router-dom';
import Chart from "react-apexcharts";
import CircularProgress from '@mui/material/CircularProgress';
import { fetchAlertsTrend as fetchAlertsTrendService } from "services/modules/ecs/data";

const VulnerabilityTrendAnalysis = ({ activeFilter, reportDate }) => {

  // Redux selectors
  const selectedCompanyContainer = useSelector((state) => state.auth.companyData);
  const dimensionsContainer = useSelector(state => state.ecsData.dimensions);

  // useParams hook
  const { category } = useParams();

  // State variables
  const [loading, setLoading] = useState(true);
  const [vulnerabilityAnalysisChatOptions, setVulnerabilityAnalysisChatOptions] = useState(null);

  let chartstate = {
    options: {
      chart: {
        fontFamily: '"Montserrat", "Open Sans", sans-serif',
        height: 275,
        type: "line",
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false
        }
      },
      markers: {
        size: 6,
      },
      stroke: {
        width: 3,
        curve: "smooth",
      },
      legend: {
        position: "top",
        markers: {
          width: 10,
          height: 10,
        },
      },
      tooltip: {
        shared: false,
      },
      yaxis: {
        title: {
          text: "Findings Count",
        },
      },
    },
    series: [],
  };


  // Fetches vulnerability trend data based on domain
  const fetchVulnerablityTrendData = async () => {
    let params = {
      "domain": selectedCompanyContainer.domain,
    }
    if (activeFilter) {
      if (activeFilter === 7) {
        params.days = activeFilter;
      }else if(activeFilter ===1){
        params.weeks = 30
      }
       else {
        activeFilter >= 14 ? params.weeks = activeFilter : params.months = activeFilter ;
      }
    }
    if (category !== "over-all" && category !== "compliance") {
      const dimension = dimensionsContainer.find(
        (dim) => dim.slug === category
      );
      params.dimension = dimension?.id;
    }
    const response = await fetchAlertsTrendService({ params });
    const monthGraphData = response.data;
    let categories =activeFilter === 1 ? getWeekNames() :  activeFilter >= 14 ? getWeekNames() : getDateNames();
    if (activeFilter === 7) {
      categories = getWeekNames();
    }

    let chartSeries = [];
    if (category !== "over-all" && category !== "compliance") {
      const dimension = dimensionsContainer.find(
        (dim) => dim.slug === category
      );
      chartSeries = dimension?.subDimensions
        ?.filter((dim) => !dim.subDimensions)
        ?.map((dim) => ({
          name: dim.label,
          data: new Array(categories?.length).fill(0),
        }));
    } else {
      chartSeries = dimensionsContainer
        ?.filter((dim) => !!dim.subDimensions)
        ?.map((dim) => ({
          name: dim.label,
          data: new Array(categories.length).fill(0),
        }));
    }
    chartstate.series = chartSeries;
    chartstate.series = chartstate.series?.map((series) => {
      // Find month-specific data for the current series
      const monthData = monthGraphData?.find(
        (dat) => dat.dimension.label === series.name
      );
      // Sort the counts based on the endDate property
      series.data = monthData?.counts
        .sort((cA, cB) => {
          if (new Date(cA.endDate).valueOf() < new Date(cB.endDate).valueOf()) {
            return -1;
          }
          if (new Date(cA.endDate).valueOf() > new Date(cB.endDate).valueOf()) {
            return 1;
          }
          return 0;
        })
        .map((count) => count.count);
      return series;
    });

    chartstate.options = {
      ...chartstate.options,
      xaxis: {
        categories,
      },
    };
    setVulnerabilityAnalysisChatOptions(chartstate);
    setLoading(false);
  };

  useEffect(() => {
    if (activeFilter) {
      setLoading(true);
      fetchVulnerablityTrendData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter, reportDate]);

  // sets the month to current month and date to the first day and formats the date
  const getDateNames = () => {
    const dateStart = new Date();
    dateStart.setMonth(dateStart.getMonth() - (activeFilter - 1));
    dateStart.setDate(1);
    const dateEnd = new Date();
    const newTimeValues = [];

    while (dateEnd > dateStart || dateStart.getMonth() === dateEnd.getMonth()) {
      const formattedDate = dateStart.toLocaleString('default', { month: 'short', year: 'numeric' });
      newTimeValues.push(formattedDate);
      dateStart.setMonth(dateStart.getMonth() + 1);
    }
    return newTimeValues;
  };

  // Formats time values according to active filter
  const getWeekNames = () => {
    if ((activeFilter >= 14) || (activeFilter === 7) || (activeFilter ===1)) {

      let timeValues = [];
      if (activeFilter === 14) {
        timeValues = ["Week 1", "Week 2"];
      } else if (activeFilter === 7) {
        timeValues = ["Day 1", "Day 2", "Day 3", "Day 4", "Day 5", "Day 6", "Day 7"];
      } else if (activeFilter === 21) {
        timeValues = ["Week 1", "Week 2", "Week 3"];
      } else if (activeFilter === 1) {
        timeValues = ["Week 1", "Week 2", "Week 3", "Week 4"];
      } else if (activeFilter === 42) {
        timeValues = ["Week 1", "Week 2", "Week 3", "Week 4", "Week 5", "Week 6"];
      }
      return timeValues;
    }
  };

  return (
    <div>
      {loading ? (
        <div className='d-flex justify-content-center align-items-center' style={{ height: '250px' }}>
          <CircularProgress size={60}/>
        </div>
      ) : (
        <Chart
          options={vulnerabilityAnalysisChatOptions ? vulnerabilityAnalysisChatOptions.options : chartstate.options}
          series={vulnerabilityAnalysisChatOptions ? vulnerabilityAnalysisChatOptions.series : chartstate.series}
          type="line"
          height="250"
        />
      )}
    </div>
  );
}

export default VulnerabilityTrendAnalysis;
